/**
 * The story shelf — the hub every peripheral surface hangs off.
 *
 * The brief set the bar at *"a `<select>` in a header is the minimum; a panel showing status and
 * length is barely more work and much better"*, and the reason it is much better is that
 * A `<select>` cannot express the two things a player actually wants to know before
 * clicking: **is this one still alive, and how far did it get.** A `<select>` of character
 * names cannot say "Ilse, 4 chapters, died of thirst" — and the run that ended badly is the
 * one worth naming, because dying is the game.
 *
 * Three shapes of row, one component:
 *
 * - **active** — the resume target. The whole row is the link.
 * - **dead** — read-only, still readable, with its cause of death as the line that describes
 * it. It is not greyed out: a finished run is the product, not a failure state.
 * - the shelf being **empty**, which is the state a new player starts in, and the only reason
 * the create call-to-action is duplicated here rather than only living on `/stories/new`.
 *
 * Deleted stories are filtered client-side as well as server-side. Belt and braces, and it
 * costs one predicate: `GET /stories` already omits them, but `status` is on the summary and
 * a shelf that could render "deleted · 4 chapters" if the filter ever moved is not worth the
 * saving.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import type { StorySummary } from '@/api/generated';
import { DeleteStoryDialog } from '@/features/stories/DeleteStoryDialog';
import { useT } from '@/i18n/context';
import type { Translate } from '@/i18n/t';
import { Button } from '@/ui/Button';
import { relativeTime } from '@/ui/time';

interface StoryShelfProps {
  stories: readonly StorySummary[];
}

export function StoryShelf({ stories }: StoryShelfProps) {
  const t = useT();
  // The dialog's subject, not a boolean: a confirmation that has to be told which story it is
  // confirming has one less way to delete the wrong one.
  const [deleting, setDeleting] = useState<StorySummary | null>(null);

  const shelf = [...stories]
    .filter((story) => story.status !== 'deleted')
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at));

  if (shelf.length === 0) {
    return (
      <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
        {t('ui.story.none')}
      </p>
    );
  }

  return (
    <>
      <ul className="flex flex-col">
        {shelf.map((story) => (
          <li key={story.id} className="border-rule border-t last:border-b">
            <StoryRow
              story={story}
              onDelete={() => {
                setDeleting(story);
              }}
            />
          </li>
        ))}
      </ul>

      <DeleteStoryDialog
        story={deleting}
        onClose={() => {
          setDeleting(null);
        }}
      />
    </>
  );
}

function StoryRow({ story, onDelete }: { story: StorySummary; onDelete: () => void }) {
  const t = useT();
  const dead = story.status === 'dead';

  return (
    <div className="flex items-center gap-3 py-3">
      <Link
        to={`/play/${story.id}`}
        className="group grow"
        // The row is the link, so the target is the whole line rather than the name — which
        // is what makes this usable with a thumb before the mobile pass touches it.
      >
        <span
          className="text-fg group-hover:text-accent block font-mono transition-colors"
          style={{ fontSize: 'var(--text-action)' }}
        >
          {story.character_name}
        </span>
        <span
          className="text-fg-faint mt-0.5 block font-mono"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {chapterCountLabel(t, story.chapter_count)}
          {' · '}
          {dead ? t('ui.story.died') : t('ui.story.in_progress')}
          {' · '}
          {relativeTime(t, story.updated_at)}
        </span>
      </Link>

      <Button
        variant="bare"
        onClick={onDelete}
        aria-label={t('ui.story.delete_aria', { name: story.character_name })}
      >
        {t('ui.story.delete')}
      </Button>
    </div>
  );
}

/**
 * Zero is not a plural form, it is a different sentence: a story that exists but has not been
 * played says so, rather than counting to nothing. Everything above it is `Intl.PluralRules`
 * through `t.plural` — which is what stops "1 chapters", and what gives Ukrainian the three
 * forms English does not have.
 */
function chapterCountLabel(t: Translate, count: number): string {
  return count === 0 ? t('ui.story.not_begun') : t.plural('ui.story.chapters', count);
}
